import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FicheHeader from "./FicheHeader";
import { FINE_TYPE_LABELS, FINE_STATUS_META, ALERT_TONE_CLS } from "./FinesView";
import { api, formatEURDecimal, formatDateFR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, FileText, X, Lock, Unlock, ExternalLink, UserCheck, Pencil } from "lucide-react";
import { confirmAction } from "../ConfirmDialog";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

export default function FineFiche({ fineId, onBack }) {
  const [fine, setFine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [acting, setActing] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/vendor/fines/${fineId}`)
      .then(({ data }) => setFine(data.fine))
      .catch(() => setFine(null))
      .finally(() => setLoading(false));
  }, [fineId]);

  const patch = async (payload, msg) => {
    setActing(true);
    try {
      const { data } = await api.patch(`/vendor/fines/${fineId}`, payload);
      setFine(data.fine);
      if (msg) toast.success(msg);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
      return false;
    } finally {
      setActing(false);
    }
  };

  const startEdit = () => {
    setForm({
      fine_type: fine.fine_type,
      offense_date: fine.offense_date || "",
      offense_location: fine.offense_location || "",
      fine_number: fine.fine_number || "",
      authority: fine.authority || "",
      points: fine.points ?? "",
      amount: fine.amount ?? "",
      increased_amount: fine.increased_amount ?? "",
      payment_due_date: fine.payment_due_date || "",
      designation_due_date: fine.designation_due_date || "",
      notes: fine.notes || "",
    });
    setEditing(true);
  };

  const save = async () => {
    const ok = await patch({
      ...form,
      points: form.points === "" ? null : parseInt(form.points, 10),
      amount: form.amount === "" ? null : parseFloat(form.amount),
      increased_amount: form.increased_amount === "" ? null : parseFloat(form.increased_amount),
      payment_due_date: form.payment_due_date || null,
      designation_due_date: form.designation_due_date || null,
    }, "Contravention mise à jour");
    if (ok) setEditing(false);
  };

  const uploadDoc = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/fines/${fineId}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      const { data } = await api.get(`/vendor/fines/${fineId}`);
      setFine(data.fine);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const deleteDoc = async (path) => {
    try {
      await api.delete(`/vendor/fines/${fineId}/document?path=${encodeURIComponent(path)}`);
      const { data } = await api.get(`/vendor/fines/${fineId}`);
      setFine(data.fine);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const openDoc = async (path) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  if (!fine) return <div className="py-16 text-center text-sm text-slate-500">Contravention introuvable.</div>;

  const st = FINE_STATUS_META[fine.status] || FINE_STATUS_META.received;
  const isClosed = fine.status === "closed";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="fine-fiche">
      <FicheHeader
        onBack={onBack}
        backLabel="Retour aux contraventions"
        title={`Contravention ${fine.reference}`}
        subtitle={`${fine.vehicle_name} · ${FINE_TYPE_LABELS[fine.fine_type] || fine.fine_type} · ${formatDateFR(fine.offense_date)}`}
        badge={
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`} data-testid="fine-status-badge">
            {st.label}
          </span>
        }
        editing={editing}
        onEdit={isClosed ? undefined : startEdit}
        onCancel={() => setEditing(false)}
        onSave={save}
        saving={acting}
        actions={
          !editing && (
            isClosed ? (
              <button onClick={() => patch({ status: "received" }, "Contravention rouverte")} disabled={acting}
                className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="fine-reopen-btn">
                <Unlock className="h-3.5 w-3.5" /> Rouvrir
              </button>
            ) : (
              <button onClick={async () => (await confirmAction({ title: "Clôturer cette contravention ?", message: "Le dossier deviendra définitivement non modifiable.", confirmLabel: "Confirmer la clôture", variant: "warning" })) && patch({ status: "closed" }, "Contravention clôturée")} disabled={acting}
                className="h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="fine-close-btn">
                <Lock className="h-3.5 w-3.5" /> Clôturer
              </button>
            )
          )
        }
        testid="fine-fiche-header"
      />

      {/* Alertes locales */}
      {(fine.alerts || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4" data-testid="fine-alerts">
          {fine.alerts.map((a, i) => (
            <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-semibold ${ALERT_TONE_CLS[a.tone] || ALERT_TONE_CLS.warning}`} data-testid={`fine-alert-${a.kind}`}>
              {a.icon} {a.label} <span className="font-normal opacity-80">— {a.detail}</span>
            </span>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 space-y-5">
          {/* ─── Détails ─── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="fine-details">
            <div className="overline mb-4">Détails de l'infraction</div>
            {editing ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select value={form.fine_type} onChange={(e) => setForm({ ...form, fine_type: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-type">
                    {Object.entries(FINE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Date d'infraction</label>
                  <input type="date" value={form.offense_date} onChange={(e) => setForm({ ...form, offense_date: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-date" />
                </div>
                <div>
                  <label className={labelCls}>Lieu</label>
                  <input value={form.offense_location} onChange={(e) => setForm({ ...form, offense_location: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-location" />
                </div>
                <div>
                  <label className={labelCls}>N° de contravention</label>
                  <input value={form.fine_number} onChange={(e) => setForm({ ...form, fine_number: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-number" />
                </div>
                <div>
                  <label className={labelCls}>Autorité émettrice</label>
                  <input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-authority" />
                </div>
                <div>
                  <label className={labelCls}>Points (info)</label>
                  <input type="number" min="0" max="12" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-points" />
                </div>
                <div>
                  <label className={labelCls}>Date limite de paiement</label>
                  <input type="date" value={form.payment_due_date} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-payment-due" />
                </div>
                <div>
                  <label className={labelCls}>Date limite de désignation</label>
                  <input type="date" value={form.designation_due_date} onChange={(e) => setForm({ ...form, designation_due_date: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-designation-due" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Notes</label>
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls + " mt-1 resize-none"} data-testid="fine-edit-notes" />
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Info l="N° de contravention" v={fine.fine_number || "—"} />
                <Info l="Autorité émettrice" v={fine.authority || "—"} />
                <Info l="Lieu" v={fine.offense_location || "—"} />
                <Info l="Points concernés" v={fine.points != null ? `${fine.points} pt${fine.points > 1 ? "s" : ""} (info)` : "—"} />
                <Info l="Limite de paiement" v={fine.payment_due_date ? formatDateFR(fine.payment_due_date) : "—"} />
                <Info l="Limite de désignation" v={fine.designation_due_date ? formatDateFR(fine.designation_due_date) : "—"} />
                {fine.notes && <p className="sm:col-span-2 text-slate-700 whitespace-pre-wrap pt-2 border-t border-slate-100" data-testid="fine-notes-text">{fine.notes}</p>}
              </div>
            )}
          </div>

          {/* ─── Désignation conducteur ─── */}
          <DesignationCard fine={fine} setFine={setFine} isClosed={isClosed} />

          {/* ─── Documents ─── */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="fine-documents">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Documents ({(fine.documents || []).length})</div>
              {!isClosed && (
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="fine-add-document-btn">
                  <FileText className="h-3.5 w-3.5" /> Ajouter
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadDoc(e.target.files[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>
            {(fine.documents || []).length === 0 ? (
              <div className="text-sm text-slate-400">Aucun document (avis de contravention, courrier de contestation…).</div>
            ) : (
              <div className="space-y-2">
                {fine.documents.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-slate-200" data-testid={`fine-document-${idx}`}>
                    <button onClick={() => openDoc(d.path)} className="flex items-center gap-2 min-w-0 text-sm text-slate-700 hover:text-violet-700">
                      <FileText className="h-4 w-4 text-violet-600 shrink-0" />
                      <span className="truncate">{d.name || "Document"}</span>
                      <ExternalLink className="h-3 w-3 text-slate-400 shrink-0" />
                    </button>
                    {!isClosed && (
                      <button onClick={() => deleteDoc(d.path)} className="h-6 w-6 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center shrink-0" data-testid={`fine-document-delete-${idx}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Colonne latérale ─── */}
        <div className="space-y-4">
          {!isClosed && !editing && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="fine-status-card">
              <div className="overline mb-3">Statut</div>
              <select
                value={fine.status}
                onChange={(e) => patch({ status: e.target.value }, "Statut mis à jour")}
                className={inputCls}
                data-testid="fine-status-select"
              >
                <option value="received">Reçue</option>
                <option value="designated">Conducteur désigné</option>
                <option value="paid">Payée</option>
                <option value="contested">Contestée</option>
              </select>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="fine-amounts">
            <div className="overline mb-3">Montants</div>
            {editing ? (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Montant (€)</label>
                  <input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-amount" />
                </div>
                <div>
                  <label className={labelCls}>Montant majoré (€)</label>
                  <input type="number" min="0" step="0.01" value={form.increased_amount} onChange={(e) => setForm({ ...form, increased_amount: e.target.value })} className={inputCls + " mt-1"} data-testid="fine-edit-increased" />
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <Info l="Amende" v={fine.amount != null ? formatEURDecimal(fine.amount) : "—"} />
                <Info l="Majorée" v={fine.increased_amount != null ? formatEURDecimal(fine.increased_amount) : "—"} />
                {fine.paid_at && <div className="text-xs text-emerald-600">Payée le {formatDateFR(fine.paid_at.substring(0, 10))}</div>}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="fine-links">
            <div className="overline mb-3">Liens</div>
            <div className="space-y-2 text-sm">
              <Link to={`/vendor/pilotage?tab=vehicles&vehicle=${fine.vehicle_id}`} className="block text-violet-600 hover:underline" data-testid="fine-vehicle-link">
                Véhicule : {fine.vehicle_name}
              </Link>
              {fine.booking_id && (
                <Link to={`/vendor/pilotage?tab=bookings&booking=${fine.booking_id}`} className="block text-violet-600 hover:underline" data-testid="fine-booking-link">
                  Réservation {fine.booking_reference || ""}
                </Link>
              )}
              {fine.closed_at && <div className="text-xs text-slate-400">Clôturée le {formatDateFR(fine.closed_at.substring(0, 10))}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Désignation du conducteur ────────────────────────────────────────────────
function DesignationCard({ fine, setFine, isClosed }) {
  const [manual, setManual] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [acting, setActing] = useState(false);

  const designate = async (payload, msg) => {
    setActing(true);
    try {
      const { data } = await api.post(`/vendor/fines/${fine.id}/designate`, payload);
      setFine(data.fine);
      toast.success(msg);
      setManual(false);
      setName("");
      setEmail("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setActing(false);
    }
  };

  const sug = fine.suggested_booking;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="fine-designation">
      <div className="overline mb-4">Désignation du conducteur</div>

      {fine.driver_name ? (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3.5" data-testid="fine-driver-designated">
          <span className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
            <UserCheck className="h-4 w-4 text-emerald-700" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">{fine.driver_name}</div>
            <div className="text-xs text-slate-500">
              {fine.driver_email && <>{fine.driver_email} · </>}
              Désigné {fine.driver_source === "auto" ? "via la réservation détectée" : "manuellement"}
              {fine.booking_reference && <> · Résa {fine.booking_reference}</>}
              {fine.designated_at && <> · le {formatDateFR(fine.designated_at.substring(0, 10))}</>}
            </div>
          </div>
          {!isClosed && (
            <button onClick={() => setManual(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline shrink-0" data-testid="fine-redesignate-btn">
              <Pencil className="h-3 w-3" /> Modifier
            </button>
          )}
        </div>
      ) : sug ? (
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3.5" data-testid="fine-driver-suggested">
          <div className="text-xs text-violet-700 font-semibold mb-1">🤖 Junago a retrouvé la réservation couvrant la date d'infraction :</div>
          <div className="text-sm font-semibold text-slate-900">{sug.client_name}</div>
          <div className="text-xs text-slate-500">{sug.client_email} · Réservation {sug.reference}</div>
          {!isClosed && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => designate({ driver_name: sug.client_name, driver_email: sug.client_email, booking_id: sug.id, source: "auto" }, "Conducteur désigné")}
                disabled={acting}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50"
                data-testid="fine-confirm-driver-btn"
              >
                {acting && <Loader2 className="h-3 w-3 animate-spin" />} <UserCheck className="h-3.5 w-3.5" /> Confirmer ce conducteur
              </button>
              <button onClick={() => setManual(true)} className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="fine-manual-driver-btn">
                Désigner quelqu'un d'autre
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-slate-500" data-testid="fine-no-suggestion">
          Aucune réservation ne couvre la date d'infraction.
          {!isClosed && (
            <button onClick={() => setManual(true)} className="ml-2 text-xs font-semibold text-violet-600 hover:underline" data-testid="fine-manual-driver-btn">
              Désigner manuellement
            </button>
          )}
        </div>
      )}

      {manual && !isClosed && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) designate({ driver_name: name, driver_email: email, source: "manual" }, "Conducteur désigné manuellement"); }}
          className="mt-3 grid sm:grid-cols-3 gap-2"
          data-testid="fine-manual-form"
        >
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Nom du conducteur *" className={inputCls} data-testid="fine-manual-name" />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optionnel)" className={inputCls} data-testid="fine-manual-email" />
          <div className="flex gap-2">
            <button type="submit" disabled={acting} className="flex-1 h-[38px] rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="fine-manual-submit">
              Désigner
            </button>
            <button type="button" onClick={() => setManual(false)} className="h-[38px] px-3 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-slate-50">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </form>
      )}

      {(fine.designation_history || []).length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100" data-testid="fine-designation-history">
          <div className={labelCls + " mb-2"}>Historique des désignations</div>
          <div className="space-y-1.5">
            {[...fine.designation_history].reverse().map((h, i) => (
              <div key={i} className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400 shrink-0" />
                <span className="font-semibold text-slate-700">{h.driver_name}</span>
                {h.source === "auto" ? "(suggestion confirmée)" : "(désignation manuelle)"}
                {h.booking_reference && <> · Résa {h.booking_reference}</>}
                {h.previous_driver && <> · remplace {h.previous_driver}</>}
                <span className="ml-auto shrink-0">{formatDateFR((h.at || "").substring(0, 10))}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ l, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{l}</span>
      <span className="font-medium text-slate-900 text-right">{v}</span>
    </div>
  );
}
